import { useEffect, useMemo, useState } from "react";
import { ShipmentRow, TableActionDropdownItem } from "@src/shipments/types";
import { useShipmentTableActions } from "@src/common/components/ShipmentsTableDropDown";

interface UseActionPaletteSearchReturn {
  query: string;
  setQuery: (value: string) => void;
  filteredActions: TableActionDropdownItem[];
  destructiveKeys: Set<string>;
}

// Owns the action list for the palette: pulls the row's actions, keeps only the
// ones the user can run (role-allowed + not disabled), and filters them by the
// search query. Resets the query whenever the palette opens or the row changes.
export function useActionPaletteSearch(
  row: ShipmentRow | undefined,
  isOpen: boolean
): UseActionPaletteSearchReturn {
  const { items, destructiveItems, isRoleType } = useShipmentTableActions(
    row,
    "shipments"
  );
  const [query, setQuery] = useState("");

  const destructiveKeys = useMemo(
    () => new Set(destructiveItems.map((item) => item.key)),
    [destructiveItems]
  );

  const visibleActions = useMemo(() => {
    if (!row) return [] as TableActionDropdownItem[];
    return items.filter(
      (item) =>
        isRoleType(item.minimumUserRoleType) &&
        !(item.disabled && item.disabled(row))
    );
  }, [items, isRoleType, row]);

  const filteredActions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return visibleActions;
    return visibleActions.filter((item) =>
      String(item.label).toLowerCase().includes(term)
    );
  }, [visibleActions, query]);

  useEffect(() => {
    setQuery("");
  }, [isOpen, row]);

  return { query, setQuery, filteredActions, destructiveKeys };
}
